import { faCreditCard, faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentMethods } from '../../hooks/usePaymentMethods';
import type { OrderLine } from '../../models/Order';
import { AdditionalInfoModal, type ModalCenterRef } from '../modals/AdditionalInfoModal';

interface AdditionalInfoProps {
  paymentMethodId?: number;
  orderId?: number;
  lines?: OrderLine[];
  statusId?: number;
  onUpdate?: () => void;
}

export const AdditionalInfo: FC<AdditionalInfoProps> = ({
  paymentMethodId,
  orderId,
  lines = [],
  statusId,
  onUpdate,
}) => {
  const { t } = useTranslation('viewOrdersModule');
  const { paymentMethods } = usePaymentMethods();
  const modalRef = useRef<ModalCenterRef>(null);

  const handleOpenModal = () => {
    modalRef.current?.openModal();
  };

  // Estados donde no se debe mostrar el botón de editar
  const nonEditableStatuses = [4, 5, 6, 7]; // En preparación, Preparado, Devuelto, Enviado
  const canEdit = !statusId || !nonEditableStatuses.includes(statusId);

  const paymentMethodName = useMemo(() => {
    if (!paymentMethodId || paymentMethods.length === 0) {
      return t('sections.additional-info.unassigned');
    }
    const method = paymentMethods.find((m) => m.id === paymentMethodId);
    return method?.name || t('sections.additional-info.unassigned');
  }, [paymentMethodId, paymentMethods, t]);

  return (
    <>
      <SectionBox className="bg-card-bg">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">{t('sections.additional-info.title')}</h2>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={handleOpenModal} className="bg-card-bg">
              <FontAwesomeIcon icon={faPen} className="!w-3 !h-3" />
            </Button>
          )}
        </div>
        <div className="space-y-4 pt-4">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hover-press">
              <FontAwesomeIcon icon={faCreditCard} className="text-icons !w-4 !h-4" />
            </div>
            <div>
              <p className="text-sm text-subtext">{t('sections.additional-info.payment-method')}</p>
              <p className="font-medium">{paymentMethodName}</p>
            </div>
          </div>
        </div>
      </SectionBox>

      <AdditionalInfoModal
        ref={modalRef}
        paymentMethodId={paymentMethodId}
        orderId={orderId}
        lines={lines}
        onUpdate={onUpdate}
      />
    </>
  );
};
